// components/portal/CampaignDashboard.tsx
'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { Plus, Trash2, Pencil } from '@/components/ui/lucide-memo';
import { InventorySearch, InventorySortDropdown } from './InventoryUI';
import { toast } from 'sonner';
import { notionStatusPillStyle } from '@/components/ui/notionColors';
import { BRAND_MARK_RAISED_SHADOW, BrandMark } from '@/components/ui/BrandMark';
import UserAvatar from '@/components/UserAvatar';
import { SourceLaunchComposer } from './SourceLaunchComposer';
import type { CampaignDTO, CampaignTargetKind, LeasingLeadDTO, PortfolioFitDTO } from '#types/types';
import { ACTIVE_SOURCING_CAMPAIGN_STATUSES } from '#lib/leasing/sourcingCampaignLifecycle';
import { campaignLabel } from '#lib/leasing/campaignCard';

// The dashboard surface. Ground, panel and card were all inside a twenty value band, which is why
// nothing read as an object sitting on anything. The card is raised and the campaign area is zoned
// into its own panel, so there are three value steps instead of one flat field.
/**
 * The dashboard card surface. Exported because the Database folders under this grid are the same
 * kind of object on the same dashboard and must not drift again: they carried their own darker
 * copy of these hexes and read as a different surface entirely.
 */
// The Launch bar's own surface, ChatGPT's composer card (owner, 2026-09-28): one flat #212121
// panel, a hairline border, a lighter step on hover. No inset highlight, no second border tone.
export const CARD_SURFACE =
  'border border-[#262626] bg-[#1B1B1B] transition-colors hover:bg-[#262626]';

interface CampaignDashboardProps {
  portfolioId: string;
  onSelect: (campaignId: string) => void;
}

const STATUS_LABELS: Record<string, { en: string; fr: string }> = {
  COMPLETE: { en: 'Done', fr: 'Terminé' },
  SUCCESS: { en: 'Done', fr: 'Terminé' },
  RUNNING: { en: 'Running', fr: 'En cours' },
  PENDING: { en: 'Pending', fr: 'En attente' },
  WAITING: { en: 'Waiting', fr: 'En attente' },
  FAILED: { en: 'Failed', fr: 'Échec' },
  ABORTED: { en: 'Aborted', fr: 'Interrompu' },
};

const formatStatus = (status: string, lang: string): string => {
  const entry = STATUS_LABELS[(status ?? '').toUpperCase()];
  if (entry) return lang === 'fr' ? entry.fr : entry.en;
  return status ? status.charAt(0) + status.slice(1).toLowerCase() : '–';
};

const formatDate = (value: string | undefined, lang: string): string => {
  if (!value) return '–';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '–';
  return date.toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const STATUS_FILTERS = ['RELEVANT', 'ALL', 'COMPLETE', 'RUNNING', 'PENDING', 'FAILED'] as const;
type StatusFilter = (typeof STATUS_FILTERS)[number];

const matchesStatusFilter = (status: string | undefined, filter: StatusFilter): boolean => {
  if (filter === 'ALL') return true;
  const s = (status ?? '').toUpperCase();
  // Default view: everything that matters, finished results AND live campaigns (a run
  // launched in another window must surface here). Only aborted/failed noise is masked.
  if (filter === 'RELEVANT') return s !== 'ABORTED' && s !== 'FAILED';
  if (filter === 'COMPLETE') return s === 'COMPLETE' || s === 'SUCCESS';
  return s === filter;
};

/**
 * One normalized preview row: a logo/icon, a name, and a score. Portfolio and asset runs map their
 * top brand fits into this shape, unit runs map their top units, and the ONE card renders all of
 * them the same way (no per-granularity drift).
 */
type TopFitEntry = { id: string; name: string; logo: string | null; score: number | null };

/**
 * A row the user (or the judge) has set aside must never headline a campaign.
 *
 * The card's "N leads" count already excludes these, so showing one as the top brand made a single
 * card contradict itself, and put a dismissed brand first, which is precisely what the user said
 * could not happen.
 */
const isSetAsideStatus = (status: string | null | undefined): boolean => {
  const normalized = String(status ?? '').trim().toUpperCase();
  return normalized === 'DISMISSED' || normalized === 'INVALIDATED' || normalized === 'DISCARDED';
};

/**
 * Top three fits for one campaign, whatever its granularity: leads by fitScore for an asset,
 * portfolio fits by score for a portfolio, unit-fits by score for a unit. Extracted so the card
 * view and the list view read from ONE fetch path and can never disagree about what a campaign's
 * best brands are.
 */
/**
 * ONE CARD, ONE SCREENFUL, NOT ONE PORTFOLIO. `enabled` is what the card has scrolled into view.
 *
 * Every card ran this on mount, and the dashboard mounts all of them: measured on production
 * 2026-08-12, opening Source on a 39-campaign portfolio fired 39 concurrent shortlist requests
 * (78 when it double-mounted), each pulling 24 rows to headline three brands. That is the stall -
 * not any one query, which answers in ~400ms, but forty of them at once on one server thread.
 * A card nobody has scrolled to asks for nothing.
 */
const useTopFits = (
  targetKind: CampaignTargetKind,
  campaignId: string,
  portfolioId: string,
  campaignUpdatedAt: string | null | undefined,
  enabled: boolean,
) => {
  const { kernel } = useKernel();
  const rescoreTick2 = useKernelStore((state) => state.sourceRescoreInFlight ?? 0);
  const leadRegistry = useKernelStore((state) => state.inventory.Lead.registry);
  const portfolioFitRegistry = useKernelStore((state) => state.inventory.PortfolioFit.registry);
  const [topIds, setTopIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const k = kernel.current;
    if (!k) return;
    if (!enabled) return;
    let cancelled = false;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing pattern in this file; changing it alters when state settles
    setLoading(true);
    void (async () => {
      try {
        const result =
          targetKind === 'ASSET'
            ? await k.queryInventory('Lead', {
                filters: { lastCampaignId: campaignId, sortBy: 'fitScore', sortDir: 'desc' },
                // Over-fetch and drop the set-aside rows below. The card's own "N leads" count
                // already excludes them, so taking the top 3 by score alone made the same card
                // say "20 leads" and then headline a brand the user had DISMISSED, which is
                // exactly the complaint that it "cannot be first".
                limit: 24,
                offset: 0,
              })
            : await k.queryInventory('PortfolioFit', {
                filters: { portfolioId, campaignId, sortBy: 'score', sortDir: 'desc' },
                // Over-fetched for the same reason as the asset card: a dismissed fit must not
                // headline a campaign whose count already excludes it.
                limit: 24,
                offset: 0,
              });
        if (!cancelled) setTopIds(Array.isArray(result?.ids) ? result.ids : []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  // Refetch the preview when the ranking could have moved. The effect used to depend only on the
  // campaign id, so the top three were fetched ONCE and then frozen: a re-rank (a /refine pass, a
  // rubric re-apply, a finished run) changed the shortlist while the dashboard kept showing the
  // brands that used to lead. The scores updated, because those come live from the registry, which
  // made it look deliberate rather than stale.
  //
  // Two triggers, because a re-rank can arrive either way: the shared in-flight counter every
  // re-score path raises, and the campaign's own updatedAt for anything that lands without it.
  }, [kernel, targetKind, campaignId, portfolioId, rescoreTick2, campaignUpdatedAt, enabled]);

  const entries = useMemo<TopFitEntry[]>(() => {
    if (targetKind === 'ASSET') {
      // The leads list query embeds brandName / brandLogoUrl, so no per-brand fetch.
      return topIds
        .map((id) => leadRegistry[id] as LeasingLeadDTO | undefined)
        .filter((lead): lead is LeasingLeadDTO => Boolean(lead))
        .filter((lead) => !isSetAsideStatus(lead.status))
        .slice(0, 3)
        .map((lead) => ({ id: lead.id, name: lead.brandName || '', logo: lead.brandLogoUrl || null, score: lead.fitScore }));
    }
    return topIds
      .map((id) => portfolioFitRegistry[id] as PortfolioFitDTO | undefined)
      .filter((fit): fit is PortfolioFitDTO => Boolean(fit))
      .filter((fit) => !isSetAsideStatus(fit.status))
      .slice(0, 3)
      .map((fit) => ({ id: fit.id, name: fit.brandName || '', logo: fit.brandLogoUrl || null, score: fit.score }));
  }, [targetKind, topIds, leadRegistry, portfolioFitRegistry]);

  return { entries, loading };
};

/**
 * What a run COVERED, in the user's words. There is one sourcing operation, so a card never
 * announces a "kind": it says how wide the run reached. Several assets are counted, one asset is
 * named, and a run with no asset at all swept the whole portfolio.
 */
export const campaignCoverageLabel = (campaign: CampaignDTO, lang: string): string => {
  const assetCount = Array.isArray(campaign.propertyIds) ? campaign.propertyIds.length : 0;
  if (assetCount > 1) return lang === 'fr' ? `${assetCount} actifs` : `${assetCount} assets`;
  const targetName = (campaign.targetName ?? '').trim();
  // A run with no named asset covers them all. That is a GRANULARITY, not a special kind of
  // list: the card says how wide the run reached, in the same vocabulary as "3 assets" and
  // "One asset" above it.
  if (assetCount === 0) return lang === 'fr' ? 'Tous les actifs' : 'All assets';
  return targetName || (lang === 'fr' ? 'Un actif' : 'One asset');
};

/** What a run's results are called, per granularity. Same wording the three cards used to carry. */
const countLabelOf = (targetKind: CampaignTargetKind, lang: string): string => {
  if (targetKind === 'ASSET') return 'leads';
  if (targetKind === 'PORTFOLIO') return 'fits';
  return lang === 'fr' ? 'unités' : 'unit fits';
};

/** The three fits reordered for a podium: runner-up, winner, third. */
const podiumOrder = (entries: TopFitEntry[]): { entry: TopFitEntry; rank: number }[] => {
  const [first, second, third] = entries.map((entry, index) => ({ entry, rank: index + 1 }));
  if (!first) return [];
  if (!second) return [first];
  if (!third) return [second, first];
  return [second, first, third];
};

/** Podium mark size: the winner reads larger, the card stays compact enough to show six at a time. */
const MARK_SIZE = (rank: number) => (rank === 1 ? 44 : 38);

/** Left to right, one mark after another, enough to read as a sequence, not enough to wait on. */
const PODIUM_STAGGER_MS = 55;

/**
 * Card view. The asset is the subject of a campaign, so it takes the title and the date drops to a
 * timestamp; the three best fits stand on a podium with the winner larger and raised. A leasing
 * manager recognises a brand by its mark long before they read its name, so the marks are sized in
 * tens of pixels here instead of the 16 they used to be.
 */
const PodiumCard = React.memo(({
  campaign,
  lang,
  onSelect,
  onContextMenu,
  onRename,
}: {
  campaign: CampaignDTO;
  lang: string;
  onSelect: (campaignId: string) => void;
  onContextMenu?: (event: React.MouseEvent, campaign: CampaignDTO, count: number) => void;
  /** Save a new name. Same call the card menu's Rename uses, so there is one write path. */
  onRename?: (campaignId: string, name: string) => Promise<void>;
}) => {
  // Purely visual, and deliberately one-way: once a card has been seen it keeps its brands rather
  // than dropping them when scrolled past. rootMargin starts the fetch just before it arrives, so
  // scrolling shows brands rather than a skeleton.
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [hasBeenOnScreen, setHasBeenOnScreen] = useState(false);
  useEffect(() => {
    if (hasBeenOnScreen) return;
    const node = cardRef.current;
    if (!node) return;
    // No IntersectionObserver (jsdom, an old browser) must never mean "no brands": fall back to
    // fetching, which is exactly the old behaviour.
    if (typeof IntersectionObserver === 'undefined') {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing pattern in this file; standing in for the observer that will never fire IS this branch's purpose
      setHasBeenOnScreen(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setHasBeenOnScreen(true);
          observer.disconnect();
        }
      },
      { rootMargin: '300px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasBeenOnScreen]);

  const { entries, loading } = useTopFits(
    campaign.targetKind,
    campaign.id,
    campaign.portfolioId,
    campaign.updatedAt,
    hasBeenOnScreen,
  );
  const isEnrichingThisCampaign = useKernelStore(
    (s) => (s.sourceEnrichInFlight ?? 0) > 0 && s.sourceSelectedCampaignId === campaign.id,
  );
  // One number whatever the granularity: the list route already reduced leads / portfolio fits /
  // unit fits to the run's own result family, excluding rows a human took off the list.
  const count = campaign.resultCount ?? campaign.totalResultCount ?? 0;
  const status = String(campaign.status ?? '').toUpperCase();
  const isRunning = status === 'RUNNING' || status === 'PENDING' || status === 'WAITING';
  const countLabel = countLabelOf(campaign.targetKind, lang);
  // The campaign's own name (config.name, stamped by the launch route from the brief). Cards
  // predating the name fall back to the target, which is what the title always was.
  const campaignName =
    typeof (campaign.config as { name?: unknown } | undefined)?.name === 'string'
      ? String((campaign.config as { name?: unknown }).name).trim()
      : '';
  const coverage = campaignCoverageLabel(campaign, lang);

  // Who launched this run. Stored on the campaign since the model was written; the id is on the
  // DTO, the name comes from the User registry, hydrated once for the whole grid by the list
  // below rather than per card - twenty cards by the same person must not be twenty fetches.
  const [isRenaming, setIsRenaming] = useState(false);
  const [draft, setDraft] = useState('');
  /** Set by Escape so the blur that follows abandons the edit instead of saving it. */
  const cancelledRef = React.useRef(false);
  const commitCardRename = useCallback(async () => {
    const next = draft.trim();
    setIsRenaming(false);
    // Unchanged or empty is a cancel, not a write: the API refuses an empty name, and sending one
    // only to show an error would be theatre.
    if (!next || !onRename || next === (campaignName || coverage)) return;
    await onRename(campaign.id, next);
  }, [draft, onRename, campaign.id, campaignName, coverage]);

  const ownerId = String((campaign as { createdByUserId?: string | null }).createdByUserId ?? '');
  const owner = useKernelStore((state) => (ownerId ? state.inventory.User?.registry?.[ownerId] : undefined));
  const ownerName = useMemo(() => {
    const o = owner as { firstName?: string | null; lastName?: string | null; email?: string } | undefined;
    if (!o) return '';
    return [o.firstName, o.lastName].filter(Boolean).join(' ').trim() || o.email || '';
  }, [owner]);
  // Progress counts completed brand analyses, not credits. The actual provider spend varies by
  // analysis cost and is not present in this DTO, so do not derive a credit figure from progress.

  // THE CARD HAS TO SHOW A SHORT RUN, because the card is where a client looks first and a run that
  // stopped early settles COMPLETE like any other. Measured on 2026-08-15: 50 brands committed, 29
  // analysed, 21 never looked at, and the card was indistinguishable from a finished run. The chat
  // line says it now, but the chat scrolls and the card does not.
  const notAnalysed = (() => {
    const progress = (campaign.metrics as
      | { progress?: { truncatedReason?: unknown; notAnalysed?: unknown } }
      | undefined)?.progress;
    if (!progress?.truncatedReason) return null;
    const left = progress.notAnalysed;
    return typeof left === 'number' && Number.isFinite(left) && left > 0 ? Math.floor(left) : null;
  })();

  const handleClick = useCallback(() => onSelect(campaign.id), [onSelect, campaign.id]);
  const handleContextMenu = useCallback(
    (event: React.MouseEvent) => onContextMenu?.(event, campaign, count),
    [onContextMenu, campaign, count],
  );

  return (
    // A DIV, not a button: the title below becomes an <input> while it is being renamed, and an
    // input inside a button is invalid HTML that never receives focus. Keyboard semantics are
    // restored explicitly so the card is still operable without a mouse. The ref is also what the
    // IntersectionObserver above watches to fetch this card's leads only once it is on screen.
    <div
      ref={cardRef}
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return;
        if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); handleClick(); }
      }}
      onContextMenu={onContextMenu ? handleContextMenu : undefined}
      className={'flex cursor-pointer flex-col gap-4 rounded-[24px] p-5 text-left ' + CARD_SURFACE}
    >
      <div className="flex items-start justify-between gap-2.5">
        <div className="flex min-w-0 items-center gap-2.5">
          {/* The asset the run is on, by its own logo (owner, 2026-09-26). */}
          {campaign.targetLogoUrl ? (
            <BrandMark logoUrl={campaign.targetLogoUrl} name={campaign.targetName ?? ''} size={32} radius={8} />
          ) : null}
          <div className="min-w-0">
          {/* fontSize pinned inline: globals.css forces every div/span to 12px from an unlayered
              rule, which beats a Tailwind text-[…] utility whatever its specificity. */}
          {isRenaming ? (
            <input
              autoFocus
              value={draft}
              // Every pointer event stops here: the card opens the campaign on click, so without
              // this, clicking into the field to fix a typo would navigate away mid-edit.
              onClick={(event) => event.stopPropagation()}
              onDoubleClick={(event) => event.stopPropagation()}
              onChange={(event) => setDraft(event.target.value)}
              onBlur={() => {
                if (cancelledRef.current) { cancelledRef.current = false; setIsRenaming(false); return; }
                void commitCardRename();
              }}
              onKeyDown={(event) => {
                event.stopPropagation();
                if (event.key === 'Enter') { event.preventDefault(); event.currentTarget.blur(); }
                if (event.key === 'Escape') { event.preventDefault(); cancelledRef.current = true; event.currentTarget.blur(); }
              }}
              className="w-full rounded-full border border-[#2A2A2A] bg-transparent px-2.5 font-medium leading-tight text-white outline-none focus:border-[#444444]"
              style={{ fontSize: 16 }}
            />
          ) : (
            <div
              className="truncate font-medium leading-tight text-white"
              style={{ fontSize: 16 }}
              title={onRename ? (lang === 'fr' ? 'Double-cliquez pour renommer' : 'Double-click to rename') : undefined}
              onDoubleClick={onRename ? (event) => {
                // The card's own click opens the campaign; renaming must not do both.
                event.stopPropagation();
                setDraft(campaignName || coverage);
                setIsRenaming(true);
              } : undefined}
            >
              {campaignName || coverage}
            </div>
          )}
          <div className="mt-1 truncate tabular-nums text-[#8E8E8E]" style={{ fontSize: 13 }}>
            {/* What the run covered stays visible when the title is the campaign's own name. */}
            {campaignName ? `${coverage} · ` : ''}
            {formatDate(campaign.createdAt, lang)}
            {isRunning ? '' : `, ${count} ${countLabel}`}
            {notAnalysed !== null && !isRunning ? (
              // Amber, not red: the run did its job within the budget it was given, and a red card
              // would read as a failure the client should report rather than a shortfall they can
              // act on by raising the ceiling.
              <span className="text-[#C9A227]">
                {lang === 'fr'
                  ? ` · ${notAnalysed} marques non analysées, budget atteint`
                  : ` · ${notAnalysed} brands not analysed, budget reached`}
              </span>
            ) : null}
          </div>
          </div>
        </div>
        <span
          className="inline-flex shrink-0 items-center rounded-full px-2.5 py-0.5"
          style={{ ...notionStatusPillStyle(isEnrichingThisCampaign ? 'RUNNING' : campaign.status), fontSize: 12 }}
        >
          {isEnrichingThisCampaign ? (lang === 'fr' ? 'Enrichissement' : 'Enriching') : formatStatus(campaign.status, lang)}
        </span>
      </div>

      {loading ? (
        <div className="flex items-start gap-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex flex-1 flex-col items-center gap-[6px]" style={{ paddingTop: i === 1 ? 0 : 8 }}>
              <div
                className="animate-pulse rounded-lg bg-[#3A3A3A]"
                style={{ width: MARK_SIZE(i === 1 ? 1 : 2), height: MARK_SIZE(i === 1 ? 1 : 2) }}
              />
              <div className="h-2.5 w-12 animate-pulse rounded bg-[#3A3A3A]" />
            </div>
          ))}
        </div>
      ) : entries.length > 0 ? (
        <div className="flex items-start gap-3">
          {podiumOrder(entries).map(({ entry, rank }, position) => (
            <div
              key={entry.id}
              className="flex min-w-0 flex-1 flex-col items-center gap-[6px]"
              style={{ paddingTop: rank === 1 ? 0 : 8 }}
            >
              {/* No rank digit: the podium's size and lift already say which is first. */}
                <BrandMark
                  logoUrl={entry.logo}
                  name={entry.name}
                  size={MARK_SIZE(rank)}
                  appear
                  appearDelayMs={position * PODIUM_STAGGER_MS}
                  raised
                />
              {/* Sizes pinned inline for the same reason as the card title: a text-[…] utility
                  loses to the unlayered 12px clamp in globals.css, which silently flattened the
                  winner's score to exactly the runners-up's. */}
              <span className="max-w-full truncate text-center text-[#CDCDCD]" style={{ fontSize: 13 }}>
                {entry.name || '–'}
              </span>
              <span
                className={'font-mono leading-none ' + (rank === 1 ? 'text-white' : 'text-[#CDCDCD]')}
                style={{ fontSize: rank === 1 ? 15 : 13 }}
              >
                {entry.score ?? '–'}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex items-center justify-center py-6 text-[#8E8E8E]" style={{ fontSize: 13 }}>
          {isRunning
            ? lang === 'fr'
              ? 'En attente de la première marque…'
              : 'Waiting on the first brand…'
            : lang === 'fr'
              ? 'Aucun fit pour le moment'
              : 'No fits yet'}
        </div>
      )}

      <div className="flex items-center gap-2.5">
        <span className="flex min-w-0 items-center gap-2.5 tabular-nums text-[#8E8E8E]" style={{ fontSize: 13 }}>
          <span>
            <span className="font-mono text-[#CDCDCD]">{count}</span> {countLabel}
          </span>
          {/* WHO RAN IT. The same fact the page header shows as a property, on the card that opens
              that page - a dashboard of runs by several people is unreadable without it. Rendered
              only once the user is known: a bare "owner" label teaches nothing and costs a line. */}
          {ownerId && ownerName ? (
            <span className="flex min-w-0 items-center gap-1.5">
              <span className="text-[#5A5A5A]">·</span>
              {/* The shared avatar, not a raw <img>: it is memoized, it signs the URL, and it
                  falls back to initials. The hand-rolled version rendered a broken-image icon for
                  every owner who has never uploaded a photo, which is most of them. */}
              <UserAvatar userId={ownerId} name={ownerName} canEdit={false} size="16px" shape="circle" />
              <span className="truncate text-[#8E8E8E]">{ownerName}</span>
            </span>
          ) : null}
        </span>
      </div>
    </div>
  );
});

// Up to 250 of these hang under one parent, and every keystroke in the toolbar search box
// re-renders that parent. Unmemoized, each keystroke re-ran 250 cards - two kernel-store
// selectors, a podium sort and three BrandMarks apiece. The props are stable by construction:
// `campaign` comes from a registry-backed useMemo, `onSelect` and `onContextMenu` are useCallbacks.
PodiumCard.displayName = 'PodiumCard';

/**
 * Studio-style dashboard of sourcing campaigns for a portfolio. A toolbar
 * (create / search / status filter / sort) sits above a grid of campaign cards,
 * each previewing its top brand fits. Selecting a card opens its fits in the
 * editor; "New campaign" hands off to the right-bar setup form.
 */
export const CampaignDashboard = ({ portfolioId, onSelect }: CampaignDashboardProps) => {
  const { kernel } = useKernel();
  const lang = useKernelStore((state) => state.language);
  // ONE registry, every granularity. The toggle below is a filter over this list, not a choice of
  // which of three lists to read, which is what used to leave the dashboard empty whenever it did
  // not happen to point at the level the campaigns were on.
  const campaignRegistry = useKernelStore((state) => state.inventory.Campaign.registry);
  // Bumped when a campaign is launched from the right-bar setup form, re-queries
  // so the freshly created campaign appears without leaving the dashboard.
  const refreshToken = useKernelStore((state) => state.sourceCampaignsRefreshToken);

  const [ids, setIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState('');
  // Default masks aborted/failed noise while keeping finished AND live campaigns visible
  // (a run launched in another window must show up here). The filter exposes all on demand.
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('RELEVANT');
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('desc');
  const userContext = useKernelStore((state) => state.userContext);
  // Deleting a campaign hard-deletes it and its leads (the API is Fater-admin only), so the
  // affordance only exists for users the API would actually accept.
  const canDeleteCampaigns = Boolean(userContext?.isFaterAdmin);
  // Right-click delete menu, anchored at the cursor. `confirming` gates the destructive call
  // behind a second, explicit click, a campaign and its leads do not come back.
  const [deleteMenu, setDeleteMenu] = useState<
    {
      campaignId: string;
      /** Granularity of the row the menu was opened on: what to delete, and what to call its results. */
      targetKind: CampaignTargetKind;
      label: string;
      status: string;
      count: number;
      /** The campaign's current name, so the rename field opens on it rather than empty. */
      name: string;
      x: number;
      y: number;
    } | null
  >(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  // Rename, in the same menu. null = not renaming; a string = the field's current contents.
  const [renameDraft, setRenameDraft] = useState<string | null>(null);
  const [isRenaming, setIsRenaming] = useState(false);

  const handleCardContextMenu = useCallback(
    (event: React.MouseEvent, campaign: CampaignDTO, count: number) => {
      event.preventDefault();
      event.stopPropagation();
      setConfirmingDelete(false);
      setRenameDraft(null);
      const currentName =
        typeof (campaign.config as { name?: unknown } | undefined)?.name === 'string'
          ? String((campaign.config as { name?: unknown }).name).trim()
          : '';
      setDeleteMenu({
        campaignId: campaign.id,
        targetKind: campaign.targetKind,
        name: currentName,
        label: currentName || formatDate(campaign.createdAt, lang) || campaign.id.slice(-6),
        status: String(campaign.status ?? '').toUpperCase(),
        count,
        // Keep the menu on-screen near the bottom/right edges.
        x: Math.min(event.clientX, (typeof window !== 'undefined' ? window.innerWidth : 1280) - 260),
        // Clamp for the TALLEST state (rename field open, delete confirm open), so the Save
        // button cannot land off-screen on a right-click near the bottom edge.
        y: Math.min(event.clientY, (typeof window !== 'undefined' ? window.innerHeight : 800) - 300),
      });
    },
    [lang],
  );

  // Dismiss on outside click / Escape.
  useEffect(() => {
    if (!deleteMenu) return;
    const close = () => { setDeleteMenu(null); setConfirmingDelete(false); setRenameDraft(null); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    window.addEventListener('mousedown', close);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', close);
      window.removeEventListener('keydown', onKey);
    };
  }, [deleteMenu]);

  /**
   * The ONE way a campaign gets renamed on this screen: the card menu and double-clicking a card
   * title both come through here.
   *
   * It also fixes a real bug in the version it replaces, which always called the SOURCING route:
   * an estate run lives in a different table with its own endpoint, so renaming one from the menu
   * hit a 404 and reported "Rename failed" for a campaign that was perfectly renameable.
   */
  // The kernel owns the rename; the card menu and the card title both call it.
  const renameCampaign = useCallback(
    async (campaignId: string, name: string) => { await kernel.current?.renameCampaign(campaignId, name); },
    [kernel],
  );

  const handleRenameCampaign = useCallback(async () => {
    if (!deleteMenu || isRenaming) return;
    const next = (renameDraft ?? '').trim();
    // Unchanged or empty is a cancel, not a write.
    if (!next || next === deleteMenu.name) {
      setRenameDraft(null);
      return;
    }
    setIsRenaming(true);
    try {
      await renameCampaign(deleteMenu.campaignId, next);
      setRenameDraft(null);
      setDeleteMenu(null);
    } finally {
      setIsRenaming(false);
    }
  }, [deleteMenu, renameDraft, isRenaming, renameCampaign]);

  const handleDeleteCampaign = useCallback(async () => {
    const k = kernel.current;
    if (!k || !deleteMenu || isDeleting) return;
    const { campaignId, targetKind } = deleteMenu;
    setIsDeleting(true);
    try {
      // ONE destroy path, the Kernel's. This used to re-do its aftermath by hand (clear the
      // selection, bump the refresh token) and stopped one step short: deleting the OPEN campaign
      // left its dead conversation up over the dashboard, which destroySourceCampaign hands back
      // to the portfolio's own conversation.
      //
      // The GRANULARITY has to travel with the id. It was hardcoded to the asset family, so a
      // portfolio campaign was sent to the asset route, which filters on targetKind and answered
      // 404: 23 of 125 campaigns could not be deleted at all. The menu has always captured the real
      // kind, it was simply only used for the label.
      await k.destroySourceCampaign(targetKind, campaignId);
      // Drop it from this list immediately; the Kernel's refresh token re-queries behind it so
      // counts and pagination settle.
      setIds((prev) => prev.filter((id) => id !== campaignId));
      setDeleteMenu(null);
      setConfirmingDelete(false);
    } catch (error: any) {
      const message = String(error?.message || '');
      toast.error(
        /active/i.test(message)
          ? lang === 'fr'
            ? 'Campagne en cours, arrêtez-la avant de la supprimer.'
            : 'Campaign is still running, stop it before deleting.'
          : message || (lang === 'fr' ? 'Échec de la suppression.' : 'Failed to delete the campaign.'),
      );
    } finally {
      setIsDeleting(false);
    }
  }, [kernel, deleteMenu, isDeleting, lang]);

  useEffect(() => {
    const k = kernel.current;
    if (!k) return;
    let cancelled = false;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing pattern in this file; changing it alters when state settles
    setIds([]);
    setIsLoading(true);
    void (async () => {
      try {
        // Flush empty campaigns before listing: fire-and-forget but awaited, so the subsequent
        // list already excludes them.
        if (portfolioId) await k.flushEmptyCampaigns(portfolioId);
        if (cancelled) return;
        // Fetch enough to actually BE the whole list. At 60 the grid silently stopped short: a
        // portfolio with 91 campaigns had 31 of them unreachable from this screen, by any click,
        // which is how a full history reads as a deleted one. 250 is the list API's own default
        // ceiling and leaves room to grow; past that the answer is paging, not a bigger number.
        // Every campaign of the viewer, whatever portfolio the Source view last pointed at: the
        // dashboard has no portfolio picker, so filtering by one hid a run launched on another
        // (2026-09-28, a Compagnie de Phalsbourg run vanished behind the Altarea default).
        const result = await k.queryInventory('Campaign', {
          filters: { sortBy: 'createdAt', sortDir: 'desc' },
          limit: 250,
          offset: 0,
          force: true,
        });
        if (cancelled) return;
        setIds(Array.isArray(result?.ids) ? result.ids : []);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [kernel, portfolioId, refreshToken]);

  const campaigns = useMemo(
    () =>
      ids
        .map((id) => campaignRegistry[id] as CampaignDTO | undefined)
        .filter((c): c is CampaignDTO => Boolean(c)),
    [ids, campaignRegistry]
  );

  // Hydrate every owner ONCE for the whole grid. Doing it per card would be one fetch per card
  // for what is usually two or three distinct people.
  // A STRING, not an array: Array.from(new Set(...)) is a new identity on every Campaign registry
  // change, which is several times a second during a live run, so the effect re-fired at socket
  // rate. Harmless while every owner resolves - and not harmless when one never can, because
  // /api/inventory/users is organisation-scoped, so a run launched by someone outside the viewer's
  // org is permanently unresolvable and was being re-requested forever with force+ttl:0.
  const ownerIdsKey = useMemo(
    () => Array.from(new Set(campaigns.map((c) => c.createdByUserId ?? '').filter(Boolean))).sort().join(','),
    [campaigns],
  );
  const requestedOwnersRef = React.useRef<Set<string>>(new Set());
  useEffect(() => {
    const wanted = ownerIdsKey ? ownerIdsKey.split(',') : [];
    const missing = wanted.filter((id) => !requestedOwnersRef.current.has(id));
    if (missing.length === 0) return;
    missing.forEach((id) => requestedOwnersRef.current.add(id));
    // ensureInventoryIds already skips ids the registry holds, so asking once per id is enough
    // and an id that cannot resolve is never asked for twice.
    void kernel.current?.ensureInventoryIds('User', missing).catch(() => undefined);
  }, [ownerIdsKey, kernel]);

  const visibleCampaigns = useMemo(() => {
    const query = search.trim().toLowerCase();
    const filtered = campaigns.filter((campaign) => {
      // Single-brand campaigns are test/debug noise on the dashboard, mask them.
      if (campaign.targetBrandCount <= 1) return false;
      if (!matchesStatusFilter(campaign.status, statusFilter)) return false;
      if (!query) return true;
      const haystack = `${campaignLabel(campaign, '')} ${campaignCoverageLabel(campaign, lang)} ${formatDate(campaign.createdAt, lang)} ${formatStatus(campaign.status, lang)}`.toLowerCase();
      return haystack.includes(query);
    });
    return filtered.sort((a, b) => {
      const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return sortDir === 'desc' ? tb - ta : ta - tb;
    });
  }, [campaigns, search, statusFilter, sortDir, lang]);

  // One section per portfolio, newest-first by each portfolio's latest run, like the Assets tab.
  const portfolioRegistry = useKernelStore((s) => s.inventory.Portfolio.registry);
  const campaignGroups = useMemo(() => {
    const groups = new Map<string, CampaignDTO[]>();
    for (const campaign of visibleCampaigns) {
      const list = groups.get(campaign.portfolioId) ?? [];
      list.push(campaign);
      groups.set(campaign.portfolioId, list);
    }
    return [...groups.entries()];
  }, [visibleCampaigns]);
  const groupPortfolioIdsKey = campaignGroups.map(([id]) => id).join(',');
  useEffect(() => {
    if (groupPortfolioIdsKey) void kernel.current?.ensureInventoryIds('Portfolio', groupPortfolioIdsKey.split(',')).catch(() => undefined);
  }, [groupPortfolioIdsKey, kernel]);

  // EVERY CAMPAIGN, BY DEFAULT. This showed the first 6 and hid the rest behind a "See all" in
  // the toolbar, which reads as "you have 6 campaigns" - the owner of an account with 91 of them
  // reasonably concluded they had been deleted. The toolbar already filters (search, status,
  // sort); the list itself should not also be a filter nobody asked for.

  const emptyState =
    !isLoading && campaigns.length === 0 ? (
      <div className="col-span-full flex flex-col items-center justify-center gap-2 px-6 py-10 text-center">
        <p className="text-[12px] text-[#6E6E6E]">
          {lang === 'fr' ? 'Aucune campagne pour le moment.' : 'No campaigns yet.'}
        </p>
      </div>
    ) : !isLoading && campaigns.length > 0 && visibleCampaigns.length === 0 ? (
      <div className="col-span-full py-8 text-center text-[12px] italic text-[#424242]">
        {lang === 'fr' ? 'Aucune campagne ne correspond.' : 'No campaigns match your filters.'}
      </div>
    ) : null;

  // Creating waits for the new session before the form opens (~410ms measured), so the button
  // shows a pending state rather than looking dead, and a second press cannot mint a second
  // session. Shared by the toolbar entry and the glowing bottom button.
  const [isCreating, setIsCreating] = useState(false);
  const handleCreate = useCallback(async () => {
    if (isCreating) return;
    setIsCreating(true);
    try {
      await kernel.current?.beginSourcingCampaignDraft();
    } finally {
      setIsCreating(false);
    }
  }, [kernel, isCreating]);
  const handleCreateClick = useCallback(() => void handleCreate(), [handleCreate]);
  const sortOptions = useMemo(() => [
    { label: lang === 'fr' ? 'Plus récentes' : 'Newest first', field: 'desc' },
    { label: lang === 'fr' ? 'Plus anciennes' : 'Oldest first', field: 'asc' },
  ], [lang]);

  return (
    <div className="flex w-full flex-col">
      {/* THE way to start a run, the Launch bar (SourceLaunchComposer, 2026-09-28): the user says
          what they need, the agent sets the campaign up. At the top, above the campaigns it
          creates, the way a chat home puts its composer first (owner, 2026-09-28). */}
      <div className="mb-6 flex justify-center px-4">
        <SourceLaunchComposer />
      </div>

      {/* Toolbar, the same as every module home's (InventoryListToolbar): status chips on the left,
          the selected one on a round lighter chip; on the right the labelled sort pill, the shared
          search field and the one white pill, "New"; 32px down to the cards. */}
      <div className="flex shrink-0 flex-wrap items-center gap-3 px-4 pb-8">
        <div className="flex min-w-0 flex-wrap items-center gap-1" role="group" aria-label={lang === 'fr' ? 'Statut' : 'Status'}>
          {STATUS_FILTERS.map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={statusFilter === option}
              onClick={() => setStatusFilter(option)}
              className={
                'h-8 rounded-full px-3 text-[14px] transition-colors ' +
                (statusFilter === option ? 'bg-white/[0.08] text-white' : 'text-[#E5E5E5] hover:bg-white/[0.05]')
              }
              style={{ fontSize: 14 }}
            >
              {option === 'ALL' ? (lang === 'fr' ? 'Tous' : 'All') : formatStatus(option, lang)}
            </button>
          ))}
        </div>

        <div className="ml-auto flex items-center gap-2">
          <InventorySortDropdown
            options={sortOptions}
            activeField={sortDir}
            activeDir={sortDir}
            onChange={(field) => setSortDir(field === 'asc' ? 'asc' : 'desc')}
            currentLabel={sortOptions.find((option) => option.field === sortDir)?.label ?? sortOptions[0]!.label}
            pill
          />
          <InventorySearch value={search} onChange={setSearch} placeholder={lang === 'fr' ? 'Rechercher des campagnes' : 'Search campaigns'} alwaysOpen />

          <button
            type="button"
            onClick={handleCreateClick}
            disabled={isCreating}
            className="inline-flex h-8 items-center gap-1.5 rounded-full bg-[#F0F0F0] px-3.5 text-[14px] font-medium text-black transition-colors hover:bg-white disabled:opacity-60"
            style={{ fontSize: 14 }}
          >
            <Plus size={16} strokeWidth={2.2} />
            {lang === 'fr' ? 'Nouvelle' : 'New'}
          </button>
        </div>
      </div>

      {/* No panel border: the raised cards already separate themselves from the ground, so the
          outline around them only drew a box the eye had to cross. Two value steps, not three. */}
      <div>
        <div className="px-4">
        {isLoading && campaigns.length === 0 ? (
          <div className="grid grid-cols-2 gap-4">
            {Array.from({ length: 4 }).map((_, index) => (
              // THE SKELETON IS THE CARD, MINUS THE DATA. This one was a rough sketch: it had no
              // subtitle, no score under each brand and no footer, so it stood about 40px shorter
              // than the thing it stands in for and the grid jumped the moment the campaigns
              // landed. Every box below mirrors a real element in PodiumCard above - same wrapper
              // (gap-3, pb-3), same podium lift, same 11.5/15px type heights, same bordered
              // footer - so the placeholder occupies exactly the space the card will.
              <div
                key={`campaign-skeleton-${index}`}
                className={'flex flex-col gap-4 rounded-[24px] p-5 ' + CARD_SURFACE}
                aria-hidden="true"
              >
                {/* Heights are the RENDERED line boxes of the real card, measured in the browser
                    (header 42, podium 109, footer 30), not the font sizes - globals.css clamps
                    type app-wide, so a 17px font does not make a 17px line. */}
                <div className="flex items-start justify-between gap-2.5">
                  <div className="min-w-0 flex-1">
                    <div className="h-[24px] w-3/4 animate-pulse rounded bg-[#3A3A3A]" />
                    <div className="mt-[3px] h-[15px] w-1/2 animate-pulse rounded bg-[#2F2F2F]" />
                  </div>
                  <div className="h-[20px] w-14 shrink-0 animate-pulse rounded-full bg-[#3A3A3A]" />
                </div>
                <div className="flex items-start gap-3">
                  {[2, 1, 3].map((rank, i) => (
                    <div
                      key={rank}
                      className="flex flex-1 flex-col items-center gap-[6px]"
                      style={{ paddingTop: i === 1 ? 0 : 8 }}
                    >
                      <div
                        className="animate-pulse rounded-lg bg-[#3A3A3A]"
                        style={{ width: MARK_SIZE(rank === 1 ? 1 : 2), height: MARK_SIZE(rank === 1 ? 1 : 2) }}
                      />
                      <div className="h-[17px] w-14 max-w-full animate-pulse rounded bg-[#3A3A3A]" />
                      {/* the score, taller on the winner exactly as the real card renders it */}
                      <div
                        className="animate-pulse rounded bg-[#2F2F2F]"
                        style={{ height: rank === 1 ? 15 : 13, width: rank === 1 ? 26 : 22 }}
                      />
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2.5">
                  <div className="h-[18px] w-16 animate-pulse rounded bg-[#2F2F2F]" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <>
          {campaignGroups.map(([groupPortfolioId, groupCampaigns]) => (
            <section key={groupPortfolioId} className="mb-8">
              <h2 className="mb-3 font-semibold text-[#ECECEC]" style={{ fontSize: 16 }}>{portfolioRegistry[groupPortfolioId]?.name ?? 'Portfolio'}</h2>
              <div className="grid grid-cols-2 gap-4">
                {groupCampaigns.map((campaign) => (
                  <PodiumCard
                    key={campaign.id}
                    campaign={campaign}
                    lang={lang}
                    onSelect={onSelect}
                    onContextMenu={handleCardContextMenu}
                    onRename={renameCampaign}
                  />
                ))}
              </div>
            </section>
          ))}
          {emptyState && <div className="grid grid-cols-2 gap-4">{emptyState}</div>}
          </>
        )}
        </div>
      </div>

      {/* Right-click delete. Two-step: the menu names what is about to go, and the destructive
          call only fires on the explicit second click. Running campaigns cannot be deleted
          (the API refuses), so the item says stop-it-first instead of failing after the fact. */}
      {deleteMenu ? (
        <div
          className="fixed z-50 w-[248px] overflow-hidden rounded-[16px] border border-white/[0.06] bg-[#353535] shadow-[0_12px_32px_rgba(0,0,0,0.55)]"
          style={{ left: deleteMenu.x, top: deleteMenu.y }}
          onMouseDown={(e) => e.stopPropagation()}
          onContextMenu={(e) => e.preventDefault()}
          role="menu"
        >
          <div className="border-b border-[#2A2A2A] px-3 py-2">
            <div className="truncate text-[12px] font-medium text-[#E8E8E8]">{deleteMenu.label}</div>
            <div className="mt-0.5 text-[11px] text-[#6E6E6E]">
              {deleteMenu.count} {countLabelOf(deleteMenu.targetKind, lang)}
            </div>
          </div>

          {/* Rename. Available to anyone who can see the campaign, at every granularity: naming
              your own runs is not an administrative act. Enter saves, Escape leaves the field, the
              API bounds the length. */}
          {renameDraft === null ? (
            <button
              type="button"
              onClick={() => setRenameDraft(deleteMenu.name)}
              className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-[12.5px] text-[#D0D0D0] transition-colors hover:bg-[#242424]"
              role="menuitem"
            >
              <Pencil size={14} />
              {lang === 'fr' ? 'Renommer' : 'Rename'}
            </button>
          ) : (
            <div className="px-3 py-2.5">
              <input
                autoFocus
                type="text"
                value={renameDraft}
                maxLength={80}
                disabled={isRenaming}
                onChange={(e) => setRenameDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void handleRenameCampaign();
                  if (e.key === 'Escape') setRenameDraft(null);
                }}
                placeholder={lang === 'fr' ? 'Nom de la campagne' : 'Campaign name'}
                className="h-[30px] w-full rounded-[7px] border border-[#3A6FF8] bg-[#1F1F1F] px-2 text-[12.5px] text-[#E8E8E8] placeholder-[#5A5A5A] outline-none disabled:opacity-50"
              />
              <div className="mt-2 flex items-center gap-2">
                <button
                  type="button"
                  disabled={isRenaming}
                  onClick={() => setRenameDraft(null)}
                  className="h-[28px] flex-1 rounded-[7px] border border-[#2A2A2A] bg-[#1F1F1F] text-[12px] text-[#B5B5B5] transition-colors hover:bg-[#262626] disabled:opacity-40"
                >
                  {lang === 'fr' ? 'Annuler' : 'Cancel'}
                </button>
                <button
                  type="button"
                  disabled={isRenaming}
                  onClick={handleRenameCampaign}
                  className="h-[28px] flex-1 rounded-[7px] bg-[#2E2E2E] text-[12px] font-medium text-[#E8E8E8] transition-colors hover:bg-[#383838] disabled:opacity-60"
                >
                  {isRenaming ? (lang === 'fr' ? 'Enregistrement…' : 'Saving…') : (lang === 'fr' ? 'Enregistrer' : 'Save')}
                </button>
              </div>
            </div>
          )}

          {/* Delete stays admin-only; the separator only appears between two real items. */}
          {canDeleteCampaigns ? <div className="border-t border-[#2A2A2A]" /> : null}
          {!canDeleteCampaigns ? null : (ACTIVE_SOURCING_CAMPAIGN_STATUSES as readonly string[]).includes(deleteMenu.status) ? (
            <div className="px-3 py-2.5 text-[11.5px] leading-relaxed text-[#B5B5B5]">
              {lang === 'fr'
                ? 'Campagne en cours. Arrêtez-la avant de la supprimer.'
                : 'This campaign is still running. Stop it before deleting.'}
            </div>
          ) : !confirmingDelete ? (
            <button
              type="button"
              onClick={() => setConfirmingDelete(true)}
              className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-[12.5px] text-[#E0603A] transition-colors hover:bg-[#241512]"
              role="menuitem"
            >
              <Trash2 size={14} />
              {lang === 'fr' ? 'Supprimer la campagne' : 'Delete campaign'}
            </button>
          ) : (
            <div className="px-3 py-2.5">
              <p className="text-[11.5px] leading-relaxed text-[#B5B5B5]">
                {lang === 'fr'
                  ? `Supprimer définitivement cette campagne et ses ${deleteMenu.count} résultats ?`
                  : `Permanently delete this campaign and its ${deleteMenu.count} results?`}
              </p>
              <div className="mt-2.5 flex items-center gap-2">
                <button
                  type="button"
                  disabled={isDeleting}
                  onClick={() => { setConfirmingDelete(false); setDeleteMenu(null); }}
                  className="h-[28px] flex-1 rounded-[7px] border border-[#2A2A2A] bg-[#1F1F1F] text-[12px] text-[#B5B5B5] transition-colors hover:bg-[#262626] disabled:opacity-40"
                >
                  {lang === 'fr' ? 'Annuler' : 'Cancel'}
                </button>
                <button
                  type="button"
                  disabled={isDeleting}
                  onClick={handleDeleteCampaign}
                  className="h-[28px] flex-1 rounded-[7px] bg-[#B34526] text-[12px] font-medium text-white transition-colors hover:bg-[#C24A2E] disabled:opacity-60"
                >
                  {isDeleting ? (lang === 'fr' ? 'Suppression…' : 'Deleting…') : (lang === 'fr' ? 'Supprimer' : 'Delete')}
                </button>
              </div>
            </div>
          )}
        </div>
      ) : null}

    </div>
  );
};

CampaignDashboard.displayName = 'CampaignDashboard';
